import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import type { DocsList, Pendientes } from "./types";
import { agregarToken, limpiarSesion } from "../features/ui/uiSlice";
const baseUrl = import.meta.env.VITE_URL_BACK;
console.log("url", baseUrl);

const baseQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("app_token");
    if (token) {
      headers.set(`Authorization`, `Bearer ${token}`);
    }
    return headers;
  },
});
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // Ejecuta la petición original
  let result = await baseQuery(args, api, extraOptions);

  // Si el backend responde que el token expiró o es inválido
  if (result.error && result.error.status === 401) {
    const refreshToken = localStorage.getItem("app_refresh_token");

    if (refreshToken) {
      const refreshResult = await baseQuery(
        {
          url: '/users/refresh',
          method: 'POST',
          body: { refreshToken }
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const data = refreshResult.data as { token: string; refreshToken: string };

        api.dispatch(agregarToken({
          token: data.token,
          refreshToken: data.refreshToken
        }));

        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(limpiarSesion());
      }
    } else {
      api.dispatch(limpiarSesion());
    }
  }

  return result;
};
export const docsApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    buscarTramite: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/buscar",
      }),
    }),
    login: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["sesion"],
        method: "post",
        url: "/users/login",
      }),
    }),
    logout: builder.mutation({
      query: () => ({
        body: {},
        invalidatesTags: ["sesion"],
        method: "post",
        url: "/users/logout",
      }),
    }),
    expirePass: builder.mutation({
      query: (post) => ({
        body: post,
        invalidatesTags: ["sesion"],
        method: "post",
        url: "/users/reset",
      }),
    }),
    validarSesion: builder.query<any, void>({
      query: () => "/users/check-status",
    }),
    equipo: builder.query<any, void>({
      query: () => "/users/equipo",
      keepUnusedDataFor: 0,
    }),
    dashboard: builder.query<any, void>({
      query: () => "/users/llamadas",
      keepUnusedDataFor: 0,
    }),
    getMenu: builder.query<any, void>({
      query: () => "/users/menu",
    }),
    verPoliza: builder.query<any, any>({
      providesTags: ["llamada"],
      query: (id) => "/polizas/" + id,
    }),
    verCobranzaPoliza: builder.query<any, any>({
      query: (id) => "/polizas/cobranza/" + id,
    }),
    verInformacionPoliza: builder.query<any, any>({
      query: (id) => "/polizas/informacion/" + id,
    }),
    guardarDatosAdicionales: builder.mutation({
      query: (post) => ({
        body: post,
        method: "put",
        url: "/polizas/muros",
      }),
    }),
    generarCas: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/levantar-cas",
      }),
      invalidatesTags: ["llamada"],
    }),
    agregarInteraccion: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/interaccion",
      }),
      invalidatesTags: ["llamada"],
    }),
    validacion: builder.query<any, any>({
      query: (id) => "/polizas/validacion/" + id,
    }),
    agregarSiniestro: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/siniestro",
      }),
      invalidatesTags: ["llamada"],
    }),
    getTipoSeguro: builder.query<any, void>({
      query: () => "/polizas/tipo-seguro",
    }),
    agregarLlamada: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/agregar-llamada",
      }),
      invalidatesTags: ["llamada"],
    }),
    agregarLlamadaBusqueda: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/agregar-llamada-busqueda",
      }),
    }),
    finalizarLlamada: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/finalizar-llamada",
      }),
      invalidatesTags: ["llamada"],
    }),
    getReportes: builder.query<any, void>({
      keepUnusedDataFor: 0,
      query: () => `/polizas/reportes`,
    }),
    generarReporte: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/reportes",
      }),
    }),
    catCancelaciones: builder.query<any, void>({
      query: () => "/polizas/cat-cancelaciones",
    }),
    Retencion: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/retencion",
      }),
      invalidatesTags: ["llamada"],
    }),
    Cancelacion: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/cancelacion",
      }),
      invalidatesTags: ["llamada"],
    }),
    Endoso: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/endoso",
      }),
      invalidatesTags: ["llamada"],
    }),
    AccionEquipo: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/users/user",
      }),
    }),
    cargarArchivoBucket: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/users/carga-archivo-bucket",
      }),
    }),
    generarCertificado: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/generar-certificado",
      }),
    }),
    enviosFisicos: builder.mutation({
      query: (post) => ({
        body: post,
        method: "post",
        url: "/polizas/envios-fisicos",
      }),
    }),
  }),
  reducerPath: "api",
  tagTypes: ["sesion", "cargaDocumento", "estatus-siniestro", "llamada"],
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useValidarSesionQuery,
  useLazyValidarSesionQuery,
  useLoginMutation,
  useExpirePassMutation,
  useVerPolizaQuery,
  useLazyVerPolizaQuery,
  useVerCobranzaPolizaQuery,
  useVerInformacionPolizaQuery,
  useGuardarDatosAdicionalesMutation,
  useAgregarInteraccionMutation,
  useGenerarCasMutation,
  useValidacionQuery,
  useAgregarSiniestroMutation,
  useBuscarTramiteMutation,
  useGetMenuQuery,
  useGetTipoSeguroQuery,
  useAgregarLlamadaMutation,
  useAgregarLlamadaBusquedaMutation,
  useFinalizarLlamadaMutation,
  useGenerarReporteMutation,
  useGetReportesQuery,
  useCatCancelacionesQuery,
  useRetencionMutation,
  useCancelacionMutation,
  useEndosoMutation,
  useLazyEquipoQuery,
  useAccionEquipoMutation,
  useLazyDashboardQuery,
  useCargarArchivoBucketMutation,
  useGenerarCertificadoMutation,
  useEnviosFisicosMutation,
  useLogoutMutation,
} = docsApi;
